import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import SEO from '../components/common/SEO';
import { Music, Users, Star, Mic, Calendar, ArrowRight, Loader2 } from 'lucide-react';
import api from '../lib/api';

const ShowChoir = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await api.get('/page-content/show-choir');
        // Convert sections array to object for easier access
        const sectionsObj = {};
        res.data.sections.forEach(s => {
          sectionsObj[s.key] = s.content;
        });
        setContent(sectionsObj);
      } catch (err) {
        console.error('Failed to load page content:', err);
        // Use defaults if API fails
        setContent(getDefaults());
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  const getDefaults = () => ({
    banner_image: '',
    section_image: '',
    hero_subtitle: "Combining the best of singing and dancing, our Show Choir brings together talented young performers to create spectacular musical performances.",
    what_is_title: "What is Show Choir?",
    what_is_p1: "Show Choir is a dynamic performing ensemble that combines choral singing with choreographed movement. It's the perfect blend of musical theatre, pop music, and dance that creates an electrifying performance experience.",
    what_is_p2: "Our Show Choir members learn to sing complex harmonies while executing synchronized choreography, developing skills that are highly sought after in the professional performing arts world.",
    feature_1: "Multi-part vocal harmonies",
    feature_2: "High-energy choreography",
    feature_3: "Team building & ensemble work",
    feature_4: "Performance opportunities",
    benefit_1_title: "Vocal Training",
    benefit_1_desc: "Develop your singing voice with harmony and blend techniques.",
    benefit_2_title: "Dance Skills",
    benefit_2_desc: "Learn choreography while singing - a challenging and rewarding skill.",
    benefit_3_title: "Teamwork",
    benefit_3_desc: "Build lasting friendships and learn to work as an ensemble.",
    benefit_4_title: "Performances",
    benefit_4_desc: "Regular opportunities to perform at events and showcases.",
    session_ages: "8-18",
    session_day: "Saturdays",
    session_duration: "1 Hour",
    session_note: "Show Choir runs as part of our regular Saturday programme. Contact us for current availability and pricing.",
    cta_title: "Ready to Join Show Choir?",
    cta_subtitle: "Contact us to find out about availability and how to audition for our Show Choir."
  });

  const c = content || getDefaults();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#FF007F] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <SEO page="showChoir" />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden">
        {/* Banner Image Background */}
        {c.banner_image && (
          <div className="absolute inset-0">
            <img 
              src={c.banner_image} 
              alt="Show Choir Banner" 
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/80 to-[#050505]" />
          </div>
        )}
        {!c.banner_image && (
          <div className="absolute inset-0 bg-gradient-to-b from-[#7000FF]/10 to-transparent" />
        )}
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              <span className="text-[#7000FF]">Show</span> Choir
            </h1>
            <p className="text-lg md:text-xl text-white/70 max-w-3xl mx-auto">
              {c.hero_subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* What is Show Choir */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
                {c.what_is_title}
              </h2>
              <p className="text-white/70 mb-4">
                {c.what_is_p1}
              </p>
              <p className="text-white/70 mb-6">
                {c.what_is_p2}
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-white/70">
                  <Music className="w-5 h-5 text-[#FF007F]" />
                  {c.feature_1}
                </li>
                <li className="flex items-center gap-3 text-white/70">
                  <Star className="w-5 h-5 text-[#7000FF]" />
                  {c.feature_2}
                </li>
                <li className="flex items-center gap-3 text-white/70">
                  <Users className="w-5 h-5 text-[#FF007F]" />
                  {c.feature_3}
                </li>
                <li className="flex items-center gap-3 text-white/70">
                  <Mic className="w-5 h-5 text-[#7000FF]" />
                  {c.feature_4}
                </li>
              </ul>
            </div>
            <div className="relative">
              {c.section_image ? (
                <div className="aspect-square rounded-2xl overflow-hidden">
                  <img 
                    src={c.section_image} 
                    alt="Show Choir Performance" 
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="aspect-square bg-gradient-to-br from-[#7000FF]/30 to-[#FF007F]/30 rounded-2xl flex items-center justify-center">
                  <div className="text-center">
                    <Mic className="w-24 h-24 text-white/30 mx-auto mb-4" />
                    <span className="text-white/50">Performance image coming soon</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-white/5">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-8 text-center">
            Benefits of Show Choir
          </h2>
          <div className="grid md:grid-cols-4 gap-6">
            <Card className="bg-[#0a0a0a] border-white/10">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#FF007F]/20 flex items-center justify-center">
                  <Music className="w-6 h-6 text-[#FF007F]" />
                </div>
                <h3 className="font-bold text-white mb-2">{c.benefit_1_title}</h3>
                <p className="text-white/60 text-sm">{c.benefit_1_desc}</p>
              </CardContent>
            </Card>
            
            <Card className="bg-[#0a0a0a] border-white/10">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#7000FF]/20 flex items-center justify-center">
                  <Star className="w-6 h-6 text-[#7000FF]" />
                </div>
                <h3 className="font-bold text-white mb-2">{c.benefit_2_title}</h3>
                <p className="text-white/60 text-sm">{c.benefit_2_desc}</p>
              </CardContent>
            </Card>
            
            <Card className="bg-[#0a0a0a] border-white/10">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#FF007F]/20 flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#FF007F]" />
                </div>
                <h3 className="font-bold text-white mb-2">{c.benefit_3_title}</h3>
                <p className="text-white/60 text-sm">{c.benefit_3_desc}</p>
              </CardContent>
            </Card>
            
            <Card className="bg-[#0a0a0a] border-white/10">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#7000FF]/20 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-[#7000FF]" />
                </div>
                <h3 className="font-bold text-white mb-2">{c.benefit_4_title}</h3>
                <p className="text-white/60 text-sm">{c.benefit_4_desc}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Session Info */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-gradient-to-r from-[#7000FF]/20 to-[#FF007F]/20 border-white/10">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-white mb-6 text-center">
                Show Choir Sessions
              </h2>
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div>
                  <p className="text-white/50 text-sm mb-1">Ages</p>
                  <p className="text-2xl font-bold text-white">{c.session_ages}</p>
                </div>
                <div>
                  <p className="text-white/50 text-sm mb-1">When</p>
                  <p className="text-2xl font-bold text-white">{c.session_day}</p>
                </div>
                <div>
                  <p className="text-white/50 text-sm mb-1">Duration</p>
                  <p className="text-2xl font-bold text-white">{c.session_duration}</p>
                </div>
              </div>
              <p className="text-white/60 text-center mt-6">
                {c.session_note}
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 text-center">
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
          {c.cta_title}
        </h2>
        <p className="text-white/60 mb-6 max-w-2xl mx-auto">
          {c.cta_subtitle}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button 
            className="bg-[#FF007F] hover:bg-[#D6006A]"
            onClick={() => window.location.href = '/contact'}
          >
            Contact Us <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <Button 
            variant="outline"
            className="border-white/30 text-white hover:bg-white/10"
            onClick={() => window.location.href = '/free-trial'}
          >
            Book Free Trial
          </Button>
        </div>
      </section>
    </div>
  );
};

export default ShowChoir;
